import { TRANSLATIONS, type LangCode } from './translations'

export const DEFAULT_LANGUAGE: LangCode = 'en'

const warnedMissing = new Set<string>()

function resolvePath(obj: Record<string, unknown>, path: string, lang: string): string {
  const keys = path.split('.')
  let current: unknown = obj
  for (const key of keys) {
    if (current == null || typeof current !== 'object') { current = undefined; break }
    current = (current as Record<string, unknown>)[key]
  }
  // Leaf is { en: "...", hi: "...", ... }. A language missing from a leaf falls
  // back to the default language; a key missing everywhere renders as itself.
  if (current != null && typeof current === 'object') {
    const map = current as Record<string, unknown>
    const own = map[lang]
    if (typeof own === 'string') return own
    const fallback = map[DEFAULT_LANGUAGE]
    if (typeof fallback === 'string') return fallback
  }
  if (import.meta.env?.DEV && !warnedMissing.has(path)) {
    warnedMissing.add(path)
    console.warn(`[i18n] missing translation key "${path}"`)
  }
  return path
}

/**
 * Translate `key` into `lang` without React. LanguageContext's `t` is this bound
 * to the current language; call it directly when a message must be rendered in
 * a language other than the one in the closure — e.g. right after switching.
 */
export function translate(lang: LangCode, key: string, vars?: Record<string, string | number>): string {
  const result = resolvePath(TRANSLATIONS as unknown as Record<string, unknown>, key, lang)
  if (!vars) return result
  // A function replacer, so a value containing `$&` or `$1` (a server message,
  // a file name) is inserted literally instead of as a replacement pattern.
  return result.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match,
  )
}
